import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { ALL, type PeriodSel } from "../utils/period";

// App-wide state shared by the tabs: which bottom tab is open, which telemetry
// source (G1 / G2 / Счётчик) and period are selected. Lives above the tabs so
// «Главная» can open «Телеметрия» on a file or an event, and «Отчёты» can build
// a PDF for whatever period the user is looking at.
export type Tab = "home" | "telemetry" | "events" | "reports" | "admin";
export type Source = "g1" | "g2" | "pa";

interface AppState {
  tab: Tab; setTab: (t: Tab) => void;
  source: Source; setSource: (s: Source) => void;
  unit: number;                       // 1 | 2, derived from source (meter → 1)
  sel: PeriodSel; setSel: (s: PeriodSel) => void;        // G1/G2 period
  paSel: PeriodSel; setPaSel: (s: PeriodSel) => void;    // meter period
  /** Open «Телеметрия» on a source and period in one go. */
  openTelemetry: (source: Source, sel?: PeriodSel) => void;
}

const Ctx = createContext<AppState | null>(null);

export function AppProvider({ children, initialTab = "home", initialSource = "g1" }: {
  children: ReactNode; initialTab?: Tab; initialSource?: Source;
}) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const [source, setSource] = useState<Source>(initialSource);
  const [sel, setSel] = useState<PeriodSel>(ALL);
  const [paSel, setPaSel] = useState<PeriodSel>(ALL);
  const value = useMemo<AppState>(() => ({
    tab, setTab, source, setSource, unit: source === "g2" ? 2 : 1, sel, setSel, paSel, setPaSel,
    openTelemetry: (s, p) => {
      setSource(s);
      if (p) { if (s === "pa") setPaSel(p); else setSel(p); }
      setTab("telemetry");
    },
  }), [tab, source, sel, paSel]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useApp outside AppProvider");
  return v;
}
